/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import _get from 'lodash/get';
import _map from 'lodash/map';
import _isArray from 'lodash/isArray';
import _reduce from 'lodash/reduce';
import _startsWith from 'lodash/startsWith';
import { fetchUtils } from 'react-admin';

import auth from '../providers/auth-service';
import apolloClient from '../services/apollo-client';

// imports models
import assetModel from '../models/asset';
import collectionModel from '../models/collection';
import themeModel from '../models/theme';
import categoryModel from '../models/category';
import oneOffCreditsModel from '../models/oneOffCredits';
import transactionsModel from '../models/transactions';
import transfersModel from '../models/transfers';

export const fetchGraphQL = async (query, resource, params) => {
  const variables = getVariables(resource, params);

  const response = await apolloClient.query({
    query: query,
    ...(variables && { variables }),
  });

  return parseResponse(response, resource);
};

export const mutateGraphQL = async (mutation, variables, resource) => {
  let response = {};
  try {
    response = await apolloClient.mutate({
      mutation,
      ...(variables && { variables }),
    });
  } catch (e) {
    console.log({ e });
  }

  if (response.errors) throw new Error(getGraphQLErrors(response.errors));

  if (resource) {
    return parseResponse(response, resource);
  }

  return response;
};

function getGraphQLErrors(errors) {
  return _reduce(
    errors,
    (result, error) => `${result}; ${error.message}`,
    ''
  ).substring(2);
}

export const fetchJson = async (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }

  const token = await auth.getAccessToken();
  options.headers.set('Authorization', `Bearer ${token}`);
  return fetchUtils.fetchJson(url, options);
};

function paramsToGraphQLSort(params) {
  const field = _get(params, 'sort.field');
  const sortDir = _get(params, 'sort.order');
  let sort;

  switch (field) {
    // available GraphQL sort options
    //  - byTitle
    //  - byCreationDate
    //  - byDownloadsUpdateDate
    //  - bySearchScore
    //  - sameAsIds

    case 'title':
      sort = 'byTitle';

      break;

    case 'search':
      sort = 'bySearchScore';
      break;

    case 'createdAt':
      sort = 'byCreationDate';
      break;

    default:
      sort = null;
  }

  return {
    ...(sort && { sort }),
    ...(sortDir && { sortDir: sortDir.toLowerCase() }),
  };
}

function getTotal(response, resource) {
  return _get(response, `data.${resource}.total`, 0);
}

function getHasMore(response, resource) {
  return _get(response, `data.${resource}.hasMore`, false);
}

export function buildData(rawData, model) {
  if (_isArray(rawData)) {
    return _map(rawData, (d) => model(d));
  } else if (rawData?.total !== undefined) {
    return {
      total: rawData.total,
      hasMore: rawData.hasMore,
      items: rawData.items ? buildData(rawData.items, model) : [],
    };
  } else {
    return model(rawData);
  }
}

export const parseResponse = (response, resource) => {
  let parsedResponse;
  switch (resource) {
    case 'assets':
      parsedResponse = {
        data: buildData(
          _get(response, 'data.assets.items', response.data.asset),
          assetModel
        ),
        hasMore: getHasMore(response, resource),
        total: getTotal(response, resource),
      };

      break;
    case 'createAsset':
      parsedResponse = buildData(
        _get(response, 'data.createAsset', response.data.asset),
        assetModel
      );
      break;

    case 'updateAsset':
      parsedResponse = buildData(
        _get(response, 'data.updateAsset'),
        assetModel
      );
      break;
    case 'deleteAsset':
      parsedResponse = _get(response, 'data');
      break;

    case 'collectionsSort':
    case 'collections':
      parsedResponse = {
        data: buildData(
          _get(response, 'data.collections.items', response.data.collection),
          collectionModel
        ),
        hasMore: getHasMore(response, resource),
        total: getTotal(response, resource),
      };
      break;

    case 'createOrUpdateCollection':
      parsedResponse = {
        data: buildData(
          _get(response, 'data.createOrUpdateCollection'),
          collectionModel
        ),
      };
      break;

    case 'themes':
      parsedResponse = {
        data: buildData(
          _get(response, 'data.themes.items', response.data.theme),
          themeModel
        ),
        hasMore: getHasMore(response, resource),
        total: getTotal(response, resource),
      };
      break;
    case 'createOrUpdateTheme':
      parsedResponse = {
        data: buildData(_get(response, 'data.createOrUpdateTheme'), themeModel),
        hasMore: getHasMore(response, resource),
        total: getTotal(response, resource),
      };
      break;

    case 'categories':
      parsedResponse = {
        data: buildData(
          _get(response, 'data.categories', response.data.collection),
          categoryModel
        ),
        hasMore: getHasMore(response, resource),
        total: getTotal(response, resource),
      };
      break;

    case 'oneOffCredits':
      parsedResponse = {
        data: buildData(
          _get(
            response,
            'data.oneOffCredits.items',
            response.data.oneOffCredits
          ),
          oneOffCreditsModel
        ),
        hasMore: getHasMore(response, resource),
        total: getTotal(response, resource),
      };
      break;

    case 'createOneOffCredits':
      parsedResponse = {
        data: buildData(
          _get(response, 'data.createOrUpdateOneOffCredit'),
          oneOffCreditsModel
        ),
      };
      break;

    case 'transactions':
      parsedResponse = {
        data: buildData(
          _get(response, 'data.transactions.items', response.data.transactions),
          transactionsModel
        ),
        hasMore: getHasMore(response, resource),
        total: getTotal(response, resource),
      };
      break;

    case 'transfers':
      parsedResponse = {
        data: buildData(
          _get(response, 'data.retrieveAllTransferInformation'),
          transfersModel
        ),
        hasMore: getHasMore(response, resource),
        total: getTotal(response, resource),
      };
      break;

    default:
      throw new Error(`Unkown resource ${resource}`);
  }

  return parsedResponse;
};

export const isRefSearch = (search) => {
  return _startsWith(search, '#');
};

export const getVariables = (resource, params) => {
  let page = _get(params, 'pagination.page');
  let limit = _get(params, 'pagination.perPage');
  let id = _get(params, 'id');
  let ids = _get(params, 'selectedIds', null) || _get(params, 'ids', null);
  let search = _get(params, 'filter.q');
  let filters = _get(params, 'filter', {});
  let extraData = _get(params, 'filter.extraData');

  if (_startsWith(search, '#')) {
    extraData = { ...extraData, ref: search };
    search = null;
  }

  if (extraData) {
    extraData = _map(extraData, (value, key) => ({
      key,
      value,
    }));

    filters = {
      ...filters,
      extraData,
    };
  }

  if (filters) delete filters.q;

  return {
    ...(page && { page: page - 1 }),
    ...(limit && { limit }),
    ...(id && { id }),
    ...(ids && { id: ids }),
    ...paramsToGraphQLSort(params),
    ...(search && { search }),
    filters: {
      // default filters is to show 'published', 'draft', 'archived' by default
      // status: ['published', 'draft', 'archived'],
      // and can be overriden by new status
      ...filters,
    },
  };
};
